import { ArrowRightAlt, Error, Launch, SwapHoriz } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import {
  ApiCategory,
  ApiLeagueEntryInlineRoundSummary,
  ApiUserInline,
} from "griptonite-api-client";
import { useMemo, useState } from "react";

import {
  useLeagueEntriesQuery,
  useLeagueQuery,
  useMergeLeagueEntriesMutation,
} from "@/hooks/competitions";
import { useCurrentVenueUuid } from "@/hooks/venues";
import formatNumber, { getNumberWithOrdinal } from "@/utils/formatNumber";

import Avatar from "../Avatar";
import { formatLocation } from "../Staff/StaffSidebar";
import { useModal } from "./useModal";

const RoundSummary = ({
  round,
  userUuid,
}: {
  round: ApiLeagueEntryInlineRoundSummary;
  userUuid: string;
}) => {
  const currentVenueUuid = useCurrentVenueUuid();
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        paddingX: 1,
        paddingY: 0.5,
        border: "1px solid",
        borderColor: "neutral.200",
        minWidth: 100,
        borderRadius: 8,
        cursor: "pointer",
      }}
      onClick={() => {
        // Navigate to the round details page for the user in a new tab
        window.open(
          `/gym/${currentVenueUuid}/events/${round.competition}/competitors/${userUuid}`,
          "_blank",
        );
      }}
    >
      <Stack alignItems="flex-start">
        <Typography>
          Position: <strong>{round.position ? getNumberWithOrdinal(round.position) : "–"}</strong>
        </Typography>
        <Typography>
          Score:{" "}
          <strong>
            {formatNumber(round.score, {
              maxDecimals: 1,
            })}
          </strong>
        </Typography>
      </Stack>
      <Box
        sx={{
          backgroundColor: "primary.100",
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Launch color="primary" fontSize="small" />
      </Box>
    </Stack>
  );
};

export default function MergeLeagueEntries({
  leagueUuid,
  sourceUuid,
  targetUuid,
  clearMergeState,
}: {
  leagueUuid: string;
  sourceUuid: string;
  targetUuid: string;
  clearMergeState: () => void;
}) {
  const entriesQuery = useLeagueEntriesQuery(leagueUuid);
  const venueUuid = useCurrentVenueUuid();
  const leagueQuery = useLeagueQuery(leagueUuid, venueUuid);

  const [isSourceTargetSwapped, setIsSourceTargetSwapped] = useState(false);

  if (isSourceTargetSwapped) {
    // Swap source and target UUIDs
    [sourceUuid, targetUuid] = [targetUuid, sourceUuid];
  }

  const sourceLeagueEntry = useMemo(() => {
    return entriesQuery.data?.find((entry) => entry.user.uuid === sourceUuid);
  }, [entriesQuery.data, sourceUuid]);
  const sourceLeagueEntryCategory = useMemo(() => {
    return leagueQuery.data?.categories.find(
      (category) => category.uuid === sourceLeagueEntry?.category,
    );
  }, [leagueQuery.data, sourceLeagueEntry]);

  const targetLeagueEntry = useMemo(() => {
    return entriesQuery.data?.find((entry) => entry.user.uuid === targetUuid);
  }, [entriesQuery.data, targetUuid]);
  const targetLeagueEntryCategory = useMemo(() => {
    return leagueQuery.data?.categories.find(
      (category) => category.uuid === targetLeagueEntry?.category,
    );
  }, [leagueQuery.data, targetLeagueEntry]);

  const rounds = leagueQuery.data?.competitions;

  const hasConflictingRounds = useMemo(() => {
    if (!sourceLeagueEntry || !targetLeagueEntry) {
      return false;
    }
    return sourceLeagueEntry.rounds.some((sourceRound) =>
      targetLeagueEntry.rounds.some(
        (targetRound) => sourceRound.competition === targetRound.competition,
      ),
    );
  }, [sourceLeagueEntry, targetLeagueEntry]);

  const hasCategoryError = useMemo(() => {
    return sourceLeagueEntryCategory?.uuid !== targetLeagueEntryCategory?.uuid;
  }, [sourceLeagueEntryCategory, targetLeagueEntryCategory]);

  const hasError = hasCategoryError || hasConflictingRounds;

  const mutation = useMergeLeagueEntriesMutation();
  const navigate = useNavigate();
  const { close } = useModal();

  if (!sourceLeagueEntry || !targetLeagueEntry) {
    return null;
  }

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>Merge Series Entries</DialogTitle>
      <DialogContent>
        {hasError ? (
          <Alert color="danger" sx={{ marginBottom: 2 }} invertedColors>
            <Error />
            <Typography level="body-md" textAlign="left">
              {hasCategoryError ? (
                <>
                  These users are in different categories. To resolve, change the categories in the
                  individual rounds first, or contact support for help.
                </>
              ) : null}
              {hasConflictingRounds && !hasCategoryError ? (
                <>
                  These users have at least one conflicting round. To proceed, navigate to the round
                  and remove the conflicting entry.
                </>
              ) : null}
            </Typography>
          </Alert>
        ) : null}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "150px 300px 60px 300px",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Box>
            <Button
              size="sm"
              color="neutral"
              variant="soft"
              startDecorator={<SwapHoriz />}
              onClick={() => setIsSourceTargetSwapped(!isSourceTargetSwapped)}
            >
              Switch
            </Button>
          </Box>
          <Box>
            <UserSummary
              user={sourceLeagueEntry.user}
              category={sourceLeagueEntryCategory}
              hasCategoryError={hasCategoryError}
            />
          </Box>
          <Box>
            <ArrowRightAlt fontSize="large" />
          </Box>
          <Box>
            <UserSummary
              user={targetLeagueEntry.user}
              category={targetLeagueEntryCategory}
              hasCategoryError={hasCategoryError}
            />
          </Box>
          {rounds?.map((round) => {
            const sourceEntryRound = sourceLeagueEntry.rounds.find(
              (r) => r.competition === round.uuid,
            );
            const targetEntryRound = targetLeagueEntry.rounds.find(
              (r) => r.competition === round.uuid,
            );
            return (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    textAlign: "right",
                    gap: 0.5,
                  }}
                >
                  <Typography>{round.name}</Typography>
                </Box>
                {sourceEntryRound ? (
                  <RoundSummary round={sourceEntryRound} userUuid={sourceUuid} />
                ) : (
                  <Box>–</Box>
                )}
                <Box>
                  {sourceEntryRound && !targetEntryRound ? <ArrowRightAlt /> : null}
                  {sourceEntryRound && targetEntryRound ? (
                    <Error
                      sx={{
                        color: "#C41C1C",
                        fontSize: 20,
                      }}
                    />
                  ) : null}
                </Box>
                {targetEntryRound ? (
                  <RoundSummary round={targetEntryRound} userUuid={targetUuid} />
                ) : (
                  <Box />
                )}
              </>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{ flex: 1 }}
          variant="solid"
          color="danger"
          onClick={() => {
            mutation.mutate(
              {
                leagueUuid: leagueUuid,
                sourceUserUuid: sourceUuid,
                targetUserUuid: targetUuid,
              },
              {
                onSuccess: () => {
                  clearMergeState();
                  navigate({
                    from: "/gym/$venueUuid/events/series/$leagueUuid",
                    to: "/gym/$venueUuid/events/series/$leagueUuid/competitors/$userUuid",
                    replace: true,
                    params: {
                      userUuid: targetUuid,
                    },
                  });
                  close();
                },
                onError: () => {
                  alert(
                    "An error occurred while merging entries. Please try again or contact support.",
                  );
                },
              },
            );
          }}
          size="lg"
          disabled={hasError || mutation.isPending}
          loading={mutation.isPending}
          loadingPosition="start"
        >
          Merge Entries
        </Button>
        <Button
          sx={{ flex: 1 }}
          variant="soft"
          color="neutral"
          onClick={() => {
            close();
          }}
          size="lg"
        >
          Cancel
        </Button>
      </DialogActions>
    </ModalDialog>
  );
}

const UserSummary = ({
  user,
  category,
  hasCategoryError = false,
}: {
  user: ApiUserInline;
  category: ApiCategory | undefined;
  hasCategoryError?: boolean;
}) => {
  return (
    <Stack direction="row" alignItems="center" gap={1} overflow="hidden">
      <Box>
        <Avatar user={user} size={80} key={user.uuid} />
      </Box>
      <Stack alignItems="flex-start">
        <Typography level="title-lg" fontWeight="bold">
          {user.firstName} {user.lastName}
        </Typography>
        {category ? (
          <Typography
            level="body-sm"
            fontWeight="bold"
            textColor={hasCategoryError ? "danger.500" : "neutral.900"}
          >
            {category.name}
          </Typography>
        ) : null}
        {user.basecamp ? (
          <Typography level="body-sm">{formatLocation(user.basecamp)}</Typography>
        ) : null}
        <Typography
          level="body-sm"
          sx={{ textOverflow: "ellipsis", overflow: "hidden" }}
          title={user.email}
        >
          {user.email}
        </Typography>
      </Stack>
    </Stack>
  );
};
